import { lazy } from 'react';

const CHUNK_ERROR_PATTERNS = [
  'failed to fetch dynamically imported module',
  'error loading dynamically imported module',
  'importing a module script failed',
  'failed to load module script',
  'unable to preload css',
  'chunkloaderror',
];

const RELOAD_MARK = 'crm.stale-build-reload-at';
const RELOAD_COOLDOWN_MS = 30_000;
const RETRY_DELAY_MS = 300;

export function isChunkLoadError(error: unknown): boolean {
  const text =
    error instanceof Error
      ? `${error.name} ${error.message}`
      : typeof error === 'string'
        ? error
        : '';
  const normalized = text.toLowerCase();
  return normalized !== '' && CHUNK_ERROR_PATTERNS.some((pattern) => normalized.includes(pattern));
}

function readReloadMark(): number {
  try {
    return Number(window.sessionStorage.getItem(RELOAD_MARK)) || 0;
  } catch {
    return 0;
  }
}

function writeReloadMark(value: number | null): void {
  try {
    if (value === null) window.sessionStorage.removeItem(RELOAD_MARK);
    else window.sessionStorage.setItem(RELOAD_MARK, String(value));
  } catch {
    return;
  }
}

export function reloadForStaleBuild({ force = false } = {}): boolean {
  const now = Date.now();
  if (!force && now - readReloadMark() < RELOAD_COOLDOWN_MS) return false;
  writeReloadMark(force ? null : now);
  window.location.reload();
  return true;
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

type LazyFactory = Parameters<typeof lazy>[0];

export function lazyWithRetry(factory: LazyFactory): ReturnType<typeof lazy> {
  return lazy(async () => {
    try {
      return await factory();
    } catch (error) {
      if (!isChunkLoadError(error)) throw error;
      await delay(RETRY_DELAY_MS);
      try {
        return await factory();
      } catch (retryError) {
        if (!reloadForStaleBuild()) throw retryError;
        return new Promise<never>(() => undefined);
      }
    }
  });
}
